import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { useDirectTheme } from '../hooks/useDirectTheme';
import { cn } from '../utils/cn';

interface IconButtonProps extends Omit<HTMLMotionProps<'button'>, 'icon'> {
  icon: React.ReactNode;
  variant?: 'default' | 'primary';
  size?: 'sm' | 'md';
  'aria-label'?: string;
}

export default function IconButton({
  icon,
  variant = 'default',
  size = 'md',
  className,
  'aria-label': ariaLabel,
  ...props
}: IconButtonProps) {
  // Ensure icon-only buttons have an aria-label
  if (!ariaLabel) {
    console.warn('IconButton is missing an aria-label. Please provide one for accessibility.');
  }
  const { getDirectColor } = useDirectTheme();

  return (
    <motion.button
      className={cn(
        'flex items-center justify-center rounded-lg',
        size === 'sm' ? 'p-1.5' : 'p-2',
        'transition-colors duration-200',
        'focus:outline-none focus:ring-2',
        className
      )}
      style={{
        color: getDirectColor(variant === 'primary' ? 'accent' : 'actionIcon'),
        backgroundColor: 'transparent',
      }}
      whileHover={{
        scale: 1.05,
        backgroundColor: getDirectColor('menuBackgroundHover'),
      }}
      whileTap={{ scale: 0.95 }}
      aria-label={ariaLabel}
      {...props}
    >
      {React.cloneElement(icon as React.ReactElement, {
        className: cn('w-4 h-4', (icon as React.ReactElement).props.className),
      })}
    </motion.button>
  );
}
